import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { RolesType } from 'src/enums/roles.enum'
import { IUser } from 'src/interfaces/auth/auth.interface'
import { BracketsHelper } from 'src/store/smart-suggestion/helpers/brackets.helper'
import { PanelsHelper } from 'src/store/smart-suggestion/helpers/panels.helper'
import { ProtectionsHelper } from 'src/store/smart-suggestion/helpers/protections.helper'
import {
  AddWarningAction,
  GenerateKitCompositionsAction,
  OpenWarningModalAction,
  RemoveWarningAction,
} from '../smart-suggestion/smart-suggestion.meta.actions'
import { SmartSuggestionMetaState } from '../smart-suggestion/smart-suggestion.meta.state'
import {
  IWarning,
  ProductTypes,
  WarningActions,
  WarningReferences,
} from '../smart-suggestion/smart-suggestion.model'
import { UserMetaState } from '../user/user.meta.state'
import { Utils } from './../../helpers/utils.helper'
import { AccessoriesHelper } from './../smart-suggestion/helpers/accessories.helper'
import {
  AddProposalUpdateAction,
  ChangeProposalBYDNumberAction,
  ChangeProposalInvoiceNumberAction,
  ChangeReferenceAction,
  RemoveAllSelectedTabItemsAction,
  RemoveFileAction,
  RemoveProposalUpdateAction,
  ResetCheckoutKitTypeAction,
  ResetInstallationValuesAction,
  ResetQuoteDataAction,
  ResetQuoteStateAction,
  SetCheckoutDataAction,
  SetCheckoutKitTypeAction,
  SetCheckoutStepAction,
  SetCommissionValuesAction,
  SetCurrentTabAction,
  SetDeliveryToAction,
  SetDiscountAction,
  SetDiscountCouponAction,
  SetExternalUserAction,
  SetInstallationValuesAction,
  SetIntegratorAction,
  SetInvoiceToAction,
  SetIsEditIntegratorAction,
  SetKitCompositionAction,
  SetOrderFeedAction,
  SetOrderInstallationStatusAction,
  SetOrderStatusAction,
  SetPaymentMethodAction,
  SetPaymentOriginAction,
  SetPaymentStatusAction,
  SetPaymentTypeAction,
  SetProductsConstraintsAction,
  SetProposalDataAction,
  SetProposalIntegratorAction,
  SetQuoteDataItemAction,
  SetRebateValueAction,
  SetShippingAction,
  SetSupplierCompatibilitiesAction,
  SetTabFilterAction,
  SetTotalRebateCreditAction,
  SetTotvsCodeAction,
  SetUploadedFilesAction,
  UpdateQuoteItemAction,
  UpdateQuoteItemQuantityAction,
  UploadFilesAction,
  SetChargerKitCompositionAction,
  UpdateChargerItemQuantityAction,
  SetStandardKitCompositionAction,
  UpdateStandardItemQuantityAction,
} from './quote.meta.actions'
import {
  CheckoutSteps,
  IControls,
  IInstallationValues,
  ILinkIntegrator,
  IPaymentMethod,
  IProductConstraint,
  IProposalData,
  IProposalUpdate,
  IQuoteData,
  IQuoteItem,
  IShipping,
  KitType,
  OriginTypes,
  QuoteMetaStateModel,
  StoreChargers,
  SummaryValues,
  wifiPlugIds,
} from './quote.meta.model'
import { CouponTypeEnum } from 'src/interfaces/coupon/coupon.interface'
import { StoreKit } from 'src/interfaces/kit/kit.interface'
import { OrderStatusEnum } from '../../enums/orderStatus.enum'

const INITIAL_STATE: QuoteMetaStateModel = {
  data: {
    panels: [],
    inverters: [],
    brackets: [],
    protections: [],
    connectors: [],
    cables: [],
    accessories: [],
  },
  checkoutData: {
    rebateValue: 0,
    commissionValues: null,
    discountCoupon: null,
    installationValues: null,
    shipping: null,
  },
  productsConstraints: [],
  tabFilters: null,
  supplierCompatibilities: null,
  kitComposition: [],
  chargerKitComposition: [],
  standarKitComposition: [],
  controls: {
    currentTab: 0,
    lastClickedTab: 0,
  },
  proposalData: null,
}

@State<QuoteMetaStateModel>({
  name: 'quoteMeta',
  defaults: { ...INITIAL_STATE },
})
@Injectable()
export class QuoteMetaState {
  constructor(
    private store: Store,
    private utils: Utils,
    private panelsHelper: PanelsHelper,
    private protectionsHelper: ProtectionsHelper,
    private bracketsHelper: BracketsHelper,
    private accessoriesHelper: AccessoriesHelper,
  ) {}

  @Selector()
  public static getState(state: QuoteMetaStateModel): QuoteMetaStateModel {
    return state
  }

  @Selector()
  public static getOverload(state: QuoteMetaStateModel): string {
    const selectedPanels = this.getSelectedPanels(state)
    const selectedInverters = this.getSelectedInverters(state)

    if (!selectedInverters.length) return null

    const totalPanelsPotency = selectedPanels.reduce(
      (acc, item) => acc + (item.potency * item.quantity) / 1000,
      0,
    )
    const totalInvertersPotency = selectedInverters.reduce(
      (acc, item) => acc + item.potency * item.quantity,
      0,
    )
    const overload = (totalPanelsPotency / totalInvertersPotency - 1) * 100
    const fixedOverload = overload.toFixed(0)
    return fixedOverload
  }

  @Selector()
  public static getAcessories(state: QuoteMetaStateModel): any {
    return state.data.accessories
  }

  @Selector()
  public static getSelectedPanel(state: QuoteMetaStateModel): any {
    return state.data.panels.find((panel) => panel.quantity > 0)
  }

  @Selector()
  public static getData(state: QuoteMetaStateModel): IQuoteData {
    return state.data
  }

  @Selector()
  public static getSelectedItems(state: QuoteMetaStateModel): any[] {
    const items = []
    for (const [_key, value] of Object.entries(state.data)) {
      items.push(...value.filter((item) => item.quantity > 0))
    }
    return items
  }

  @Selector()
  public static getTotalPotency(state: QuoteMetaStateModel): number {
    const kitType = state.checkoutData.kitType

    if (kitType === KitType.STANDARD) {
      return (
        state.standarKitComposition.reduce(
          (acc, item) => acc + item.quantity * item.totalPotency,
          0,
        ) || 0
      )
    }

    const items =
      kitType === KitType.CHARGER
        ? state.chargerKitComposition
        : state.data.panels.filter((panel) => panel.quantity > 0)

    return (
      items.reduce((acc, item) => acc + item.quantity * item.potency, 0) || 0
    )
  }

  @Selector()
  public static getSelectedItemsTypeModel(state: QuoteMetaStateModel): string {
    const kitType = this.getKitType(state)

    if (kitType === KitType.STANDARD) return 'Standard Kit'

    if (kitType === KitType.CHARGER) return 'EV Charger'

    const selectedItems = this.getSelectedItems(state)
    const selectedPanels = selectedItems.filter(
      (item) => item.type === ProductTypes.PANEL,
    ).length
    const selectedInverter = selectedItems.filter(
      (item) => item.type === ProductTypes.INVERTER,
    ).length
    const selectedPlugWifi = selectedItems.filter((item) =>
      wifiPlugIds.includes(item?.id),
    ).length
    const selectedItemsOrigin =
      selectedItems.filter((item) => item.type === ProductTypes.PANEL)[0]
        ?.origin || OriginTypes.NATIONAL
    const simpleKitQuantityItems =
      selectedPanels + selectedInverter + selectedPlugWifi
    const isFullKit = selectedItems.length > simpleKitQuantityItems
    const isSimpleKit = selectedPanels > 0 && selectedInverter > 0
    const isOnlyPanel = selectedPanels > 0 && selectedInverter === 0
    if (isFullKit) {
      return `Full Kit BYD ${
        selectedItemsOrigin[0] + selectedItemsOrigin.slice(1).toLowerCase()
      }`
    }
    if (isSimpleKit) {
      return `Simple Kit BYD ${
        selectedItemsOrigin[0] + selectedItemsOrigin.slice(1).toLowerCase()
      }`
    }

    return `BYD Panel ${
      selectedItemsOrigin[0] + selectedItemsOrigin.slice(1).toLowerCase()
    }`
  }

  @Selector()
  public static getSelectedPanels(state: QuoteMetaStateModel): any {
    return state.data.panels.filter((panel) => panel.quantity > 0)
  }

  @Selector()
  public static getSelectedInverters(state: QuoteMetaStateModel): any {
    return state.data.inverters.filter((inverter) => inverter.quantity > 0)
  }

  @Selector()
  public static getSelectedProtections(state: QuoteMetaStateModel): any {
    return state.data.protections.filter(
      (protection) => protection.quantity > 0,
    )
  }

  @Selector()
  public static getSelectedBrackets(state: QuoteMetaStateModel): any {
    return state.data.brackets.filter((bracket) => bracket.quantity > 0)
  }

  @Selector()
  public static getSelectedAccessories(state: QuoteMetaStateModel): any {
    return state.data.accessories.filter((accessory) => accessory.quantity > 0)
  }

  @Selector()
  public static getSelectedInverterAddOns(state: QuoteMetaStateModel): any {
    const selectedItems = this.getSelectedItems(state)
    return selectedItems.filter(
      (item) =>
        item.type !== ProductTypes.PANEL && item.type !== ProductTypes.INVERTER,
    )
  }

  @Selector()
  public static getPanels(state: QuoteMetaStateModel): any {
    return state.data.panels
  }

  @Selector()
  public static getInverters(state: QuoteMetaStateModel): any {
    return state.data.inverters
  }

  @Selector()
  public static getProtections(state: QuoteMetaStateModel): any {
    return state.data.protections
  }

  @Selector()
  public static getBrackets(state: QuoteMetaStateModel): any {
    return state.data.brackets
  }

  @Selector()
  public static getConnectors(state: QuoteMetaStateModel): any {
    return state.data.connectors
  }

  @Selector()
  public static getCables(state: QuoteMetaStateModel): any {
    return state.data.cables
  }

  @Selector()
  public static getAccessories(state: QuoteMetaStateModel): any {
    return state.data.accessories
  }

  @Selector()
  public static getEquipmentsPotency(state: QuoteMetaStateModel): number {
    const kitType = this.getKitType(state)

    if (kitType === KitType.STANDARD) {
      const items = this.getStandardKitComposition(state)

      return items.reduce(
        (acc, item) => acc + Number(item.totalPotency) * item.quantity,
        0,
      )
    }

    const items =
      kitType === KitType.CUSTOM
        ? this.getSelectedPanels(state)
        : this.getChargerKitComposition(state)

    return items.reduce(
      (acc, item) =>
        acc + Number(item.potency || item.totalPotency) * item.quantity,
      0,
    )
  }

  @Selector()
  public static getEquipmentPrice(state: QuoteMetaStateModel): number {
    const kitType = this.getKitType(state)
    const equipmentPriceMatrix = {
      [KitType.STANDARD]: this.getStandardEquipmentPrice,
      [KitType.CHARGER]: this.getChargerEquipmentPrice,
      [KitType.CUSTOM]: this.getCustomEquipmentPrice,
    }

    return equipmentPriceMatrix[kitType](state)
  }

  /**
   * Returns the total price of the proposal without rebate, considering the total price,
   * installation costs, shipping costs and discount coupon.
   *
   * POLICY: BYD2-603
   * Description: The discount coupon must be applied to the total amount, excluding installation costs.
   *
   * @param state The state of the quote meta model.
   * @returns The total price of the proposal without rebate.
   */
  @Selector()
  public static getProposalTotalPriceWithoutRebate(
    state: QuoteMetaStateModel,
  ): number {
    const equipmentPrice = this.getEquipmentPrice(state)
    const installationValues = this.getInstallationValues(state)
    const shipping = this.getShipping(state)
    const total =
      equipmentPrice +
      (installationValues?.price || 0) +
      (shipping?.shippingCost || 0)
    const discountType = this.getDiscountType(state)
    if (!discountType) {
      return total
    }
    const totalWithoutInstallation = total - (installationValues?.price || 0)
    const discountValue = this.getDiscount(state)
    const discount =
      discountType === CouponTypeEnum.PERCENTAGE
        ? totalWithoutInstallation * (discountValue / 100)
        : discountValue
    const result = total - discount
    return result < 0 ? 0 : result
  }

  /**
   * Returns the value of the discount applied to the proposal based on the discount type and discount value.
   *
   * @param state The state of the quote meta model.
   * @returns The value of the discount applied to the proposal.
   */
  @Selector()
  public static getDiscountValue(state: QuoteMetaStateModel): number {
    const discountType = this.getDiscountType(state)
    const discount = this.getDiscount(state) || 0
    if (discountType === CouponTypeEnum.VALUE || !discountType) {
      return discount
    }
    const total = this.getProposalTotalPriceWithoutRebate(state)
    const installationValues = this.getInstallationValues(state)
    const totalWithoutInstallation = total - (installationValues?.price || 0)
    return (
      totalWithoutInstallation / (1 - discount / 100) - totalWithoutInstallation
    )
  }

  @Selector()
  public static getProposalTotalPrice(state: QuoteMetaStateModel): number {
    const rebate = this.getRebateValue(state)
    const commissionValues = this.getCommissionValues(state) || 0
    const commission = commissionValues ? commissionValues.price : 0
    const total = this.getProposalTotalPriceWithoutRebate(state)
    const totalWithDiscount = total + commission - (rebate || 0) || 0
    return totalWithDiscount
  }

  @Selector()
  public static getCostPerWatt(state: QuoteMetaStateModel): number {
    const totalPotency = this.getEquipmentsPotency(state) || 0
    const totalPrice = this.getProposalTotalPrice(state) || 0
    return totalPrice / totalPotency
  }

  @Selector()
  public static getUnityPrice(state: QuoteMetaStateModel): number {
    const panel = this.getSelectedPanel(state)
    const totalPotency = panel
      ? panel.quantity * Number(panel.potency.replace('W', ''))
      : 0
    const selectedItems = this.getSelectedItems(state)
    const totalPrice = selectedItems.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0,
    )
    const unityPrice = totalPotency

    return unityPrice ? totalPrice / totalPotency : 0
  }

  @Selector()
  public static getCheckoutData(state: QuoteMetaStateModel): any {
    return state.checkoutData
  }

  @Selector()
  public static getInvoiceTo(state: QuoteMetaStateModel): any {
    return state.checkoutData.invoiceTo
  }

  @Selector()
  public static getDeliveryTo(state: QuoteMetaStateModel): any {
    return state.checkoutData.deliveryTo
  }

  @Selector()
  public static getCheckoutStep(state: QuoteMetaStateModel): CheckoutSteps {
    return state.checkoutData.step
  }

  @Selector()
  public static getInstallationValues(
    state: QuoteMetaStateModel,
  ): IInstallationValues {
    return state.checkoutData.installationValues
  }

  @Selector()
  public static getDiscount(state: QuoteMetaStateModel): number {
    return state.checkoutData.discount
  }

  @Selector()
  public static getDiscountType(state: QuoteMetaStateModel): string {
    return state.checkoutData.discountType
  }

  @Selector()
  public static getRebateValue(state: QuoteMetaStateModel): number {
    return state.checkoutData.rebateValue
  }

  @Selector()
  public static getTotalRebateCredit(state: QuoteMetaStateModel): number {
    return state.checkoutData.availableRebateCredit
  }

  @Selector()
  public static getPaymentMethod(state: QuoteMetaStateModel): IPaymentMethod {
    return state.checkoutData.paymentMethod
  }

  @Selector()
  public static getControls(state: QuoteMetaStateModel): IControls {
    return state.controls
  }

  @Selector()
  public static getCurrentTab(state: QuoteMetaStateModel): number {
    return state.controls.currentTab
  }

  @Selector()
  public static getLastClickedTab(state: QuoteMetaStateModel): number {
    return state.controls.lastClickedTab
  }

  @Selector()
  public static getPayment(state: QuoteMetaStateModel): any {
    return {
      paymentMethod: state.checkoutData.paymentMethod,
      paymentOrigin: state.checkoutData.paymentOrigin,
      paymentType: state.checkoutData.paymentType,
    }
  }

  @Selector()
  public static getProposalData(state: QuoteMetaStateModel): IProposalData {
    return state.proposalData
  }

  @Selector()
  public static getProposalUpdates(
    state: QuoteMetaStateModel,
  ): IProposalUpdate[] {
    return state.proposalData?.orderFeed
  }

  @Selector()
  public static getProposalStatus(state: QuoteMetaStateModel): string {
    return state.proposalData?.status
  }

  @Selector()
  public static getIsProposalNotLoss(state: QuoteMetaStateModel): boolean {
    return state.proposalData?.status !== OrderStatusEnum.LOSS
  }

  @Selector()
  public static getUploadedFiles(state: QuoteMetaStateModel): any[] {
    return state.proposalData?.uploadedFiles
  }

  @Selector()
  public static getPaymentStatus(state: QuoteMetaStateModel): string {
    return state.proposalData?.paymentStatus
  }

  @Selector()
  public static getInstallationStatus(state: QuoteMetaStateModel): string {
    return state.proposalData?.installationStatus
  }

  @Selector()
  public static getDiscountCoupon(state: QuoteMetaStateModel): any {
    return state.checkoutData.discountCoupon
  }

  @Selector()
  public static getShipping(state: QuoteMetaStateModel): IShipping {
    return state.checkoutData.shipping
  }

  @Selector()
  public static getProductsConstraints(
    state: QuoteMetaStateModel,
  ): IProductConstraint[] {
    return state.productsConstraints
  }

  @Selector()
  public static getTabFilters(state: QuoteMetaStateModel): any {
    return state.tabFilters
  }

  @Selector()
  public static getSupplierCompatibilities(state: QuoteMetaStateModel): any {
    return state.supplierCompatibilities
  }

  @Selector()
  public static getTotalWeight(state: QuoteMetaStateModel): number {
    return state.proposalData?.totalWeight
  }

  @Selector()
  public static getProposalbydNumber(state: QuoteMetaStateModel): any {
    return state.proposalData?.bydNumber
  }

  @Selector()
  public static getReference(state: QuoteMetaStateModel): string {
    return state.proposalData?.reference
  }

  @Selector()
  public static getExternalUser(state: QuoteMetaStateModel): IUser {
    return state.checkoutData.externalUser
  }

  @Selector()
  public static getIntegrator(state: QuoteMetaStateModel): ILinkIntegrator {
    return state.checkoutData.integrator
  }

  @Selector()
  public static getProposalDataExternalUser(state: QuoteMetaStateModel): IUser {
    return state.proposalData?.externalUser
  }

  @Selector()
  public static getPackagingValue(state: QuoteMetaStateModel): number {
    const panels = this.getSelectedPanel(state)
    if (
      panels?.origin === OriginTypes.IMPORTED &&
      panels?.quantity % 31 === 0
    ) {
      return 0
    }
    if (
      panels?.origin === OriginTypes.NATIONAL &&
      panels?.quantity % 30 === 0
    ) {
      return 0
    }
    return 300
  }

  @Selector()
  public static getCommissionValues(
    state: QuoteMetaStateModel,
  ): IInstallationValues {
    return state.checkoutData.commissionValues
  }

  @Selector()
  public static getProposalIntegrator(
    state: QuoteMetaStateModel,
  ): ILinkIntegrator {
    return state.proposalData?.integrator
  }

  @Selector()
  public static getProposalOwner(
    state: QuoteMetaStateModel,
  ): IUser | undefined {
    return state.proposalData?.owner
  }

  @Selector()
  public static getProposalDataTotalPrice(state: QuoteMetaStateModel): number {
    return state.proposalData?.totalPrice
  }

  @Selector()
  public static getCheckoutDataKitType(state: QuoteMetaStateModel): KitType {
    return state.checkoutData.kitType
  }

  @Selector()
  private static getKitType(state: QuoteMetaStateModel): KitType {
    const orderKitContents = this.getProposalData(state)?.orderKitContents

    if (orderKitContents) {
      if (!orderKitContents[0].kitType) {
        return orderKitContents[0].isKit ? KitType.STANDARD : KitType.CUSTOM
      }

      return orderKitContents[0].kitType
    }

    const checkoutDataKitType = this.getCheckoutDataKitType(state)

    return checkoutDataKitType || KitType.CUSTOM
  }

  @Selector()
  public static getStandardKitComposition(
    state: QuoteMetaStateModel,
  ): StoreKit[] {
    const { standarKitComposition } = state
    return standarKitComposition
  }

  @Selector()
  public static getChargerKitComposition(
    state: QuoteMetaStateModel,
  ): StoreChargers[] {
    const { chargerKitComposition } = state
    return chargerKitComposition
  }

  @Selector()
  public static getKitComposition(state: QuoteMetaStateModel): IQuoteItem[] {
    const { kitComposition } = state
    return kitComposition
  }

  @Selector()
  public static getStandardEquipmentPrice(state: QuoteMetaStateModel): number {
    const proposalData = this.getProposalData(state)
    const standardKitComposition = this.getStandardKitComposition(state)

    const contentValues =
      proposalData && !standardKitComposition.length
        ? proposalData.orderKitContents.reduce(
            (acc, item) => acc + item.totalPrice,
            0,
          )
        : standardKitComposition.reduce(
            (acc, item) => acc + item.quantity * item.value,
            0,
          )

    return contentValues || 0
  }

  @Selector()
  public static getCustomEquipmentPrice(state: QuoteMetaStateModel): number {
    const proposalData = this.getProposalData(state)
    const selectedItems = this.getSelectedItems(state)

    const equipmentPrice =
      proposalData && !selectedItems.length
        ? proposalData.orderKitContents.reduce(
            (acc, item) => acc + item.totalPrice,
            0,
          )
        : selectedItems.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0,
          )

    const packaging = this.getPackagingValue(state)
    const totalPrice = equipmentPrice + packaging
    return totalPrice || 0
  }

  @Selector()
  public static getChargerEquipmentPrice(state: QuoteMetaStateModel): number {
    const proposalData = this.getProposalData(state)
    const chargerKitComposition = this.getChargerKitComposition(state)

    const equipmentPrice =
      proposalData && !chargerKitComposition.length
        ? proposalData.orderKitContents.reduce(
            (acc, item) => acc + item.totalPrice,
            0,
          )
        : chargerKitComposition.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0,
          )

    return equipmentPrice || 0
  }

  @Selector()
  public static getSelectedKitCompositionItems(
    state: QuoteMetaStateModel,
  ): any[] {
    const kitType = this.getKitType(state)

    if (kitType === KitType.STANDARD) {
      return state.standarKitComposition
    }

    if (kitType === KitType.CHARGER) {
      return state.chargerKitComposition
    }

    return this.getSelectedItems(state)
  }

  @Action(ResetQuoteStateAction)
  resetState(ctx: StateContext<QuoteMetaStateModel>): void {
    ctx.setState({ ...INITIAL_STATE })
  }

  @Action(RemoveAllSelectedTabItemsAction)
  removeAllSelectedTabItems(
    ctx: StateContext<QuoteMetaStateModel>,
    action: RemoveAllSelectedTabItemsAction,
  ): void {
    const state = ctx.getState()
    const foundData = state.data[action.payload]
    if (!foundData) {
      return
    }
    const updatedData = foundData.map((item) => ({
      ...item,
      quantity: 0,
    }))
    ctx.patchState({
      data: {
        ...state.data,
        [action.payload]: updatedData,
      },
    })
  }

  @Action(SetSupplierCompatibilitiesAction)
  setSupplierCompatibilities(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetSupplierCompatibilitiesAction,
  ): void {
    ctx.patchState({
      supplierCompatibilities: action.payload,
    })
  }

  @Action(SetTabFilterAction)
  setTabFilter(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetTabFilterAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      tabFilters: {
        ...state.tabFilters,
        [action.payload.tab]: action.payload.filters,
      },
    })
    this.store.dispatch(new GenerateKitCompositionsAction())
  }

  @Action(SetProductsConstraintsAction)
  setProductsConstraints(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetProductsConstraintsAction,
  ): void {
    ctx.patchState({
      productsConstraints: action.payload,
    })
  }

  @Action(SetQuoteDataItemAction)
  setQuoteDataItem(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetQuoteDataItemAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      data: {
        ...state.data,
        [action.payload.item]: action.payload.data,
      },
    })
  }

  @Action(UpdateQuoteItemQuantityAction)
  updateQuoteItemQuantity(
    ctx: StateContext<QuoteMetaStateModel>,
    action: UpdateQuoteItemQuantityAction,
  ): void {
    const state = ctx.getState()
    const data = state.data[action.payload.item]
    const index = data.findIndex((x) => x.id === action.payload.id)
    data[index] = {
      ...data[index],
      quantity: action.payload.quantity,
    }
    ctx.setState({
      ...state,
      data: {
        ...state.data,
        [action.payload.item]: data,
      },
      checkoutData: {
        ...state.checkoutData,
      },
    })
    this.getUpdatedSelectedItemsAndSaveAtKitComposition()
  }

  @Action(UpdateQuoteItemAction)
  updateQuoteItem(
    ctx: StateContext<QuoteMetaStateModel>,
    action: UpdateQuoteItemAction,
  ): void {
    const state = ctx.getState()
    const data = state.data[action.payload.item]
    const index = data.findIndex((x) => x.id === action.payload.data.id)
    data[index] = action.payload.data
    ctx.setState({
      ...state,
      data: {
        ...state.data,
        [action.payload.item]: data,
      },
      checkoutData: {
        ...state.checkoutData,
      },
    })
    this.store.dispatch(new GenerateKitCompositionsAction())
    this.getUpdatedSelectedItemsAndSaveAtKitComposition()
  }

  getUpdatedSelectedItemsAndSaveAtKitComposition(): void {
    const selectedItems = this.store.selectSnapshot(
      QuoteMetaState.getSelectedItems,
    )
    this.store.dispatch(new SetKitCompositionAction(selectedItems))
  }

  @Action(SetCheckoutDataAction)
  setCheckoutDataDataAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetCheckoutDataAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        ...action.payload,
      },
    })
  }

  @Action(SetInvoiceToAction)
  setInvoiceToAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetInvoiceToAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        invoiceTo: action.payload,
      },
    })
  }

  @Action(SetDeliveryToAction)
  setDeliveryToAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetDeliveryToAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        deliveryTo: action.payload,
      },
    })
  }

  @Action(SetInstallationValuesAction)
  setInstallationValuesAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetInstallationValuesAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        installationValues: action.payload,
      },
    })
  }

  @Action(SetDiscountAction)
  SetDiscountAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetDiscountAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        discount: action.payload.discount,
        discountType: action.payload.discountType,
      },
    })
  }

  @Action(SetRebateValueAction)
  setRebateValueAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetRebateValueAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        rebateValue: action.payload,
      },
    })
  }

  @Action(SetTotalRebateCreditAction)
  setTotalRebateCreditAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetTotalRebateCreditAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        availableRebateCredit: action.payload,
      },
    })
  }

  @Action(SetCheckoutStepAction)
  setCheckoutStepAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetCheckoutStepAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        step: action.payload,
      },
    })
  }

  @Action(SetPaymentMethodAction)
  setPaymentMethodAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetPaymentMethodAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        paymentMethod: action.payload,
      },
    })
  }

  @Action(SetPaymentOriginAction)
  setPaymentOriginAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetPaymentOriginAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        paymentOrigin: action.payload,
      },
    })
  }

  @Action(SetPaymentTypeAction)
  setPaymentTypeAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetPaymentTypeAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        paymentType: action.payload,
      },
    })
  }

  @Action(SetProposalDataAction)
  setProposalDataAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetProposalDataAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: action.payload,
    })
  }

  @Action(AddProposalUpdateAction)
  addProposalUpdateAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: AddProposalUpdateAction,
  ): void {
    const state = ctx.getState()
    const found = state.proposalData?.orderFeed.find(
      (update: any) => update.id === action.payload.id,
    )
    if (found) {
      return
    }
    state.proposalData?.orderFeed.unshift(action.payload)
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        orderFeed: [...state.proposalData?.orderFeed],
      },
    })
  }

  @Action(RemoveProposalUpdateAction)
  removeProposalUpdateAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: RemoveProposalUpdateAction,
  ): void {
    const state = ctx.getState()
    const orderFeed = state.proposalData?.orderFeed.filter(
      (update: any) => update.id !== action.payload,
    )
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        orderFeed,
      },
    })
  }

  @Action(UploadFilesAction)
  uploadFilesAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: UploadFilesAction,
  ): void {
    const state = ctx.getState()
    state.proposalData?.uploadedFiles.unshift(...action.payload)
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        uploadedFiles: [...state.proposalData?.uploadedFiles],
      },
    })
  }

  @Action(RemoveFileAction)
  removeFileAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: RemoveFileAction,
  ): void {
    const state = ctx.getState()
    const uploadedFiles = state.proposalData?.uploadedFiles.filter(
      (file: any) => file.id !== action.payload,
    )
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        uploadedFiles,
      },
    })
  }

  @Action(SetDiscountCouponAction)
  setDiscountCouponAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetDiscountCouponAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        discountCoupon: action.payload,
      },
    })
  }

  @Action(SetShippingAction)
  setShippingAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetShippingAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        shipping: action.payload,
      },
    })
  }

  @Action(SetOrderFeedAction)
  setOrderFeedAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetOrderFeedAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        orderFeed: action.payload,
      },
    })
  }

  @Action(SetPaymentStatusAction)
  setPaymentStatusAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetPaymentStatusAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        paymentStatus: action.payload,
      },
    })
  }

  @Action(SetOrderStatusAction)
  setOrderStatusAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetOrderStatusAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        status: action.payload,
      },
    })
  }

  @Action(SetOrderInstallationStatusAction)
  setOrderInstallationStatusAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetOrderInstallationStatusAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        installationStatus: action.payload,
      },
    })
  }

  @Action(SetUploadedFilesAction)
  setUploadedFilesAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetUploadedFilesAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        uploadedFiles: action.payload,
      },
    })
  }

  @Action(ChangeProposalBYDNumberAction)
  changeProposalBYDNumberAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: ChangeProposalBYDNumberAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        bydNumber: action.payload,
      },
    })
  }

  @Action(ChangeProposalInvoiceNumberAction)
  changeProposalInvoiceNumberAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: ChangeProposalInvoiceNumberAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        customInvoiceCode: action.payload,
      },
    })
  }

  @Action(ChangeReferenceAction)
  changeReferenceAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: ChangeReferenceAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        reference: action.payload,
      },
    })
  }

  @Action(SetTotvsCodeAction)
  setTotvsCodeAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetOrderStatusAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        totvsCode: action.payload,
      },
    })
  }

  @Action(ResetQuoteDataAction)
  resetQuoteData(ctx: StateContext<QuoteMetaStateModel>): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      data: INITIAL_STATE.data,
    })
  }

  @Action(SetExternalUserAction)
  setExternalUserAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetExternalUserAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        externalUser: action.payload,
      },
    })
  }

  @Action(SetIntegratorAction)
  setIntegratorAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetIntegratorAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        integrator: action.payload,
      },
    })
  }

  @Action(SetKitCompositionAction)
  setKitCompositionAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetKitCompositionAction,
  ): void {
    const filteredPayload = action.payload.filter((item) => item.quantity > 0)
    ctx.patchState({
      kitComposition: filteredPayload,
    })
    this.store.dispatch(new GenerateKitCompositionsAction())
  }

  @Action(SetCurrentTabAction)
  setCurrentTabAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetCurrentTabAction,
  ): void {
    const state = ctx.getState()
    const tab = action.payload
    ctx.patchState({
      controls: {
        ...state.controls,
        lastClickedTab: tab,
      },
    })
    const macroConstraintsLockedReasons = this.validateMacroConstraints(tab)
    const isMacroConstraintsLocked = macroConstraintsLockedReasons.length > 0
    const isDealer =
      this.store.selectSnapshot(UserMetaState.getUserRoles)[0] ===
      RolesType.DEALER
    if (isMacroConstraintsLocked && !isDealer) {
      console.warn(
        'MACRO CONSTRAINTS LOCKED REASONS',
        macroConstraintsLockedReasons,
      )
      macroConstraintsLockedReasons.forEach((reason) => {
        const randomIdString = Math.random().toString(36).substring(7)
        const payload: IWarning = {
          id: randomIdString,
          content: reason.content,
          isRestrictive: reason.isRestrictive,
          lockedTabs: reason.lockedTabs,
          options: reason.options,
          reference: reason.reference,
        }
        this.store.dispatch(new AddWarningAction(payload))
      })
      this.store.dispatch(new OpenWarningModalAction())
      return
    }
    ctx.patchState({
      controls: {
        ...state.controls,
        currentTab: tab,
      },
    })
    this.store.dispatch(new GenerateKitCompositionsAction())
  }

  @Action(SetCommissionValuesAction)
  setCommissionValuesAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetCommissionValuesAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        commissionValues: action.payload,
      },
    })
  }

  @Action(SetIsEditIntegratorAction)
  setIsEditIntegratorAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetIsEditIntegratorAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        isEditIntegratorActive: action.payload,
      },
    })
  }

  @Action(SetProposalIntegratorAction)
  setProposalIntegratorAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetProposalIntegratorAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      proposalData: {
        ...state.proposalData,
        integrator: action.payload,
      },
    })
  }

  @Action(ResetInstallationValuesAction)
  resetInstallationValuesAction(ctx: StateContext<QuoteMetaStateModel>): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        installationValues: null,
      },
    })
  }

  @Action(SetCheckoutKitTypeAction)
  setCheckoutKitTypeAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetCheckoutKitTypeAction,
  ): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        kitType: action.kitType,
      },
    })
  }

  @Action(ResetCheckoutKitTypeAction)
  resetCheckoutKitTypeAction(ctx: StateContext<QuoteMetaStateModel>): void {
    const state = ctx.getState()
    ctx.setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        kitType: undefined,
      },
    })
  }

  @Action(SetChargerKitCompositionAction)
  setChargerKitCompositionAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetChargerKitCompositionAction,
  ): void {
    ctx.patchState({
      chargerKitComposition: action.payload,
    })
  }

  @Action(UpdateChargerItemQuantityAction)
  updateChargerItemQuantityAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: UpdateChargerItemQuantityAction,
  ): void {
    const { chargerKitComposition } = ctx.getState()
    const { id, quantity } = action.payload

    this.updateItemQuantity({
      items: chargerKitComposition,
      id,
      quantity,
    })

    ctx.patchState({
      chargerKitComposition,
    })
  }

  @Action(SetStandardKitCompositionAction)
  setStandardKitCompositionAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: SetStandardKitCompositionAction,
  ): void {
    ctx.patchState({
      standarKitComposition: action.payload,
    })
  }

  @Action(UpdateStandardItemQuantityAction)
  updateStandardItemQuantityAction(
    ctx: StateContext<QuoteMetaStateModel>,
    action: UpdateStandardItemQuantityAction,
  ): void {
    const { standarKitComposition } = ctx.getState()
    const { id, quantity } = action.payload

    this.updateItemQuantity({
      items: standarKitComposition,
      id,
      quantity,
    })

    ctx.patchState({
      standarKitComposition,
    })
  }

  private updateItemQuantity({
    items,
    id,
    quantity,
  }: {
    items: any[]
    id: string
    quantity?: number
  }) {
    const index = items.findIndex((item) => item.id === id)

    if (index === -1) return

    items[index] = {
      ...items[index],
      quantity: quantity || items[index].quantity + 1,
    }
  }

  validateMacroConstraints(tab: number): any[] {
    const macroConstraintsLockedReasons = []
    const hasSelectedPanels = this.validateSelectedPanels()
    const hasSelectedInvertersAddOns = this.validateSelectedInverterAddOns()
    const hasSelectedInverters = this.validateSelectedInverters()
    if (tab === 1 || tab === 7) {
      if (!hasSelectedPanels) {
        const content = this.utils.getTranslatedKey(
          'You cannot proceed to this tab cause you do not have panels selected, you must select panels to proceed to this tab.',
        )
        const reason = {
          content,
          isRestrictive: true,
          lockedTabs: [1, 7],
          options: [
            {
              actions: [WarningActions.GO_TO_PANELS_TAB],
              title: 'Select Panels',
            },
          ],
          reference: WarningReferences.MACRO_CONSTRAINTS_EMPTY_PANELS,
        }
        macroConstraintsLockedReasons.push(reason)
      } else {
        const foundWarnings = this.findWarningReasonsByReference(
          WarningReferences.MACRO_CONSTRAINTS_EMPTY_PANELS,
        )
        if (foundWarnings.length > 0) {
          this.store.dispatch(
            new RemoveWarningAction(foundWarnings[0].reference),
          )
        }
      }
    }
    if (tab > 1 && tab < 7) {
      const hasInverterAndPanelSelected =
        this.validateInverterAndPanelSelected()
      if (!hasInverterAndPanelSelected) {
        const content = this.utils.getTranslatedKey(
          'You cannot proceed to this tab cause you do not have panels and inverters selected, you must select both to proceed to this tab.',
        )
        const reason = {
          content,
          isRestrictive: true,
          lockedTabs: [2, 3, 4, 5, 6, 7],
          options: [
            {
              actions: [
                hasSelectedPanels
                  ? WarningActions.GO_TO_INVERTERS_TAB
                  : WarningActions.GO_TO_PANELS_TAB,
              ],
              title: hasSelectedPanels ? 'Select Inverters' : 'Select Panels',
            },
          ],
          reference:
            WarningReferences.MACRO_CONSTRAINTS_EMPTY_PANELS_AND_INVERTERS,
        }
        macroConstraintsLockedReasons.push(reason)
      } else {
        const foundWarnings = this.findWarningReasonsByReference(
          WarningReferences.MACRO_CONSTRAINTS_EMPTY_PANELS_AND_INVERTERS,
        )
        if (foundWarnings.length > 0) {
          this.store.dispatch(
            new RemoveWarningAction(foundWarnings[0].reference),
          )
        }
      }
    }
    if (tab !== 1 && tab !== 0) {
      const panelReasons = this.panelsHelper.validatePanelsConstraints()
      macroConstraintsLockedReasons.push(...panelReasons)
    }
    if (tab === 7) {
      if (!hasSelectedInverters && hasSelectedInvertersAddOns) {
        const content = this.utils.getTranslatedKey(
          'You cannot proceed to this tab cause you do not have inverter selected and have inverter add ons selected, you must select inverter to proceed to this tab.',
        )
        const reason = {
          content,
          isRestrictive: true,
          lockedTabs: [7],
          options: [
            {
              actions: [WarningActions.GO_TO_INVERTERS_TAB],
              title: 'Select Inverters',
            },
          ],
          reference: WarningReferences.MACRO_CONSTRAINTS_EMPTY_INVERTERS,
        }
        macroConstraintsLockedReasons.push(reason)
      }
      const bracketsReasons = this.bracketsHelper.validateBracketsConstraints()
      const protectionsReasons =
        this.protectionsHelper.validateProtectionsConstraints()
      const accessoriesReasons =
        this.accessoriesHelper.validateAccessoriesConstraints()
      macroConstraintsLockedReasons.push(
        ...protectionsReasons,
        ...bracketsReasons,
        ...accessoriesReasons,
      )
    }
    return macroConstraintsLockedReasons
  }

  validateSelectedPanels(): boolean {
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    return selectedPanel
  }

  validateSelectedInverters(): boolean {
    const selectedInverters = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverters,
    )
    return !!selectedInverters.length
  }

  validateInverterAndPanelSelected(): boolean {
    const selectedPanels = this.validateSelectedPanels()
    const inverters = this.store.selectSnapshot(QuoteMetaState.getInverters)
    const selectedInverters = inverters.some(
      (inverter) => inverter.quantity > 0,
    )
    return selectedPanels && selectedInverters
  }

  validateSelectedInverterAddOns(): boolean {
    const selectedInverterAddOns = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverterAddOns,
    )
    return !!selectedInverterAddOns.length
  }

  findWarningReasonsByReference(reference: WarningReferences): IWarning[] {
    const warnings = this.store.selectSnapshot(
      SmartSuggestionMetaState.getWarnings,
    )
    const foundWarnings = warnings.filter(
      (warning) => warning.reference === reference,
    )
    return foundWarnings
  }
}
